'use client'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SharingScope from '../common/SharingScope'
import { lifeEventApi } from './life-event-api'
import { ProgressSteps } from './progress-steps'
import { EventTypeSelector } from './event-type-selector'
import { BasicInformation } from './basic-information'
import { StoryDetails } from './story-details'
import { ActionBar } from './action-bar'
import { EducationBasicInformation } from './education/basic-information'
import { EducationStoryDetails } from './education/story-details'
import { GraduationDetails } from './education/graduation-details'
import { GraduationStoryDetails } from './education/graduation-story-details'
import { AchievementsDetails } from './education/achievements-details'
import { AchievementsStoryDetails } from './education/achievements-story-details'
import { CareerMilestonesDetails } from './career/career-milestones-details'
import { CareerMilestonesStoryDetails } from './career/career-milestones-story-details'
import { JobChangesDetails } from './career/job-changes-details'
import { JobChangesStoryDetails } from './career/job-changes-story-details'
import { ProfessionalAchievementsDetails } from './career/professional-achievements-details'
import { ProfessionalAchievementsStoryDetails } from './career/professional-achievements-story-details'
import { FamilyMilestonesDetails } from './relationship/family-milestones-details'
import { FamilyMilestonesStoryDetails } from './relationship/family-milestones-story-details'
import { PersonalRelationshipsDetails } from './relationship/personal-relationships-details'
import { PersonalRelationshipsStoryDetails } from './relationship/personal-relationships-story-details'
import { SocialConnectionsDetails } from './relationship/social-connections-details'
import { SocialConnectionsStoryDetails } from './relationship/social-connections-story-details'
import { HomeLocationDetails } from './life-events/home-location-details'
import { HomeLocationStoryDetails } from './life-events/home-location-story-details'
import { TravelAdventureDetails } from './life-events/travel-adventure-details'
import { TravelAdventureStoryDetails } from './life-events/travel-adventure-story-details'
import { GeneralLifeEventsDetails } from './life-events/general-life-events-details'
import { GeneralLifeEventsStoryDetails } from './life-events/general-life-events-story-details'

export function CreateEvent() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedEventType, setSelectedEventType] = useState(null)
  const [formData, setFormData] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [sharingScope, setSharingScope] = useState('single_family')
  const [sharingFamilyIds, setSharingFamilyIds] = useState([])

  function handleEventTypeSelect(newEventType) {
    setCurrentStep(1)
    setSelectedEventType(newEventType)
  }

  function handleNext() {
    if (currentStep === 1 && !selectedEventType) return
    if (currentStep < 3) setCurrentStep(prev => prev + 1)
  }

  function handleBack() {
    if (currentStep > 1) setCurrentStep(prev => prev - 1)
  }

  async function handleSubmit() {
    if (currentStep !== 3) return handleNext()

    setIsSubmitting(true)
    setError(null)

    try {
      const eventData = {
        category: selectedEventType.category,
        title: selectedEventType.title,
        eventType: selectedEventType.type,
        date: formData.date,
        location: formData.location,
        weight: formData.weight,
        height: formData.height,
        story_details: formData.story_details || {},
        status: 'draft',
        sharing_scope: sharingScope,
        family_ids: sharingFamilyIds,
      }

      await lifeEventApi.createLifeEvent(eventData)
      navigate('/dashboard')
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleFormDataChange(newData) {
    setFormData(prev => {
      const updated = {
        ...prev,
        ...newData,
        // If there's story_details in both prev and newData, merge them
        story_details: {
          ...(prev.story_details || {}),
          ...(newData.story_details || {})
        }
      }
      return updated
    })
  }

  function renderStepContent() {
    if (!selectedEventType) return null

    return (
      <div className="space-y-12">
        {/* Step 2 content - Basic Information */}
        {currentStep >= 2 && (
          <div>
            {selectedEventType.category === 'Education' && selectedEventType.title === 'Graduation' ? (
              <GraduationDetails onFormDataChange={handleFormDataChange} />
            ) : selectedEventType.category === 'Education' && selectedEventType.title === 'Achievement' ? (
              <AchievementsDetails onFormDataChange={handleFormDataChange} />
            ) : selectedEventType.title === 'Career Milestones' ? (
              <CareerMilestonesDetails onFormDataChange={handleFormDataChange} />
            ) : selectedEventType.title === 'Job Changes' ? (
              <JobChangesDetails onFormDataChange={handleFormDataChange} />
            ) : selectedEventType.title === 'Professional Achievements' ? (
              <ProfessionalAchievementsDetails onFormDataChange={handleFormDataChange} />
            ) : selectedEventType.category === 'Education' ? (
              <EducationBasicInformation onFormDataChange={handleFormDataChange} />
            ) : selectedEventType.category === 'Relationship' && selectedEventType.title === 'Family Milestones' ? (
              <FamilyMilestonesDetails onFormDataChange={handleFormDataChange} />
            ) : selectedEventType.category === 'Relationship' && selectedEventType.title === 'Personal Relationships' ? (
              <PersonalRelationshipsDetails onFormDataChange={handleFormDataChange} />
            ) : selectedEventType.category === 'Relationship' && selectedEventType.title === 'Social Connections' ? (
              <SocialConnectionsDetails onFormDataChange={handleFormDataChange} />
            ) : selectedEventType.category === 'Life Events' && selectedEventType.title === 'Home & Location' ? (
              <HomeLocationDetails onFormDataChange={handleFormDataChange} />
            ) : selectedEventType.category === 'Life Events' && selectedEventType.title === 'Travel & Adventure' ? (
              <TravelAdventureDetails onFormDataChange={handleFormDataChange} />
            ) : selectedEventType.category === 'Life Events' && selectedEventType.title === 'General Life Events' ? (
              <GeneralLifeEventsDetails onFormDataChange={handleFormDataChange} />
            ) : (
              <BasicInformation 
                eventType={selectedEventType} 
                onFormDataChange={handleFormDataChange} 
              />
            )}
          </div>
        )}

        {/* Step 3 content - Story Details */}
        {currentStep >= 3 && (
          <div>
            {selectedEventType.category === 'Education' && selectedEventType.title === 'Graduation' ? (
              <GraduationStoryDetails />
            ) : selectedEventType.category === 'Education' && selectedEventType.title === 'Achievement' ? (
              <AchievementsStoryDetails />
            ) : selectedEventType.title === 'Career Milestones' ? (
              <CareerMilestonesStoryDetails />
            ) : selectedEventType.title === 'Job Changes' ? (
              <JobChangesStoryDetails />
            ) : selectedEventType.title === 'Professional Achievements' ? (
              <ProfessionalAchievementsStoryDetails />
            ) : selectedEventType.category === 'Education' ? (
              <EducationStoryDetails />
            ) : selectedEventType.category === 'Relationship' && selectedEventType.title === 'Family Milestones' ? (
              <FamilyMilestonesStoryDetails />
            ) : selectedEventType.category === 'Relationship' && selectedEventType.title === 'Personal Relationships' ? (
              <PersonalRelationshipsStoryDetails />
            ) : selectedEventType.category === 'Relationship' && selectedEventType.title === 'Social Connections' ? (
              <SocialConnectionsStoryDetails />
            ) : selectedEventType.category === 'Life Events' && selectedEventType.title === 'Home & Location' ? (
              <HomeLocationStoryDetails />
            ) : selectedEventType.category === 'Life Events' && selectedEventType.title === 'Travel & Adventure' ? (
              <TravelAdventureStoryDetails />
            ) : selectedEventType.category === 'Life Events' && selectedEventType.title === 'General Life Events' ? (
              <GeneralLifeEventsStoryDetails />
            ) : (
              <StoryDetails 
                eventType={selectedEventType} 
                onFormDataChange={handleFormDataChange}
              />
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F5DC]">
      <header className="bg-[#556B2F] h-16 px-8 flex items-center fixed top-0 w-full z-50">
        <h1 className="text-white text-2xl font-bold">Create Life Event</h1>
      </header>

      <main className="flex-1 overflow-y-auto pt-24 pb-32 relative z-0">
        {error && (
          <div className="max-w-7xl mx-auto px-8 mb-4">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-12">
            <ProgressSteps currentStep={currentStep} />
          </div>

          <div className="space-y-12">
            {/* Always show EventTypeSelector */}
            <div>
              <EventTypeSelector 
                selectedType={selectedEventType} 
                onSelect={handleEventTypeSelect}
              />
            </div>

            {/* Show accumulated step content */}
            {renderStepContent()}

            {/* Sharing Scope */}
            <SharingScope
              value={sharingScope}
              onChange={(scope, familyIds) => {
                setSharingScope(scope);
                setSharingFamilyIds(familyIds);
              }}
              className="mt-4"
            />
          </div>
        </div>
      </main>

      <ActionBar 
        onBack={handleBack}
        onNext={handleSubmit}
        showBack={currentStep > 1}
        showNext={true}
        disableNext={(currentStep === 1 && !selectedEventType) || isSubmitting}
        currentStep={currentStep}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}
